import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import i18n from 'i18n-js';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import {
    Box,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    TextField,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

import agent from 'api/agent';
import globalStore from 'store/globalStore';

interface NewRestaurantModalProps {
    isVisible: boolean;
    onClose: () => void;
}

interface FormValues {
    type: 'none' | 'show';
    selection: IdName | null;
}

export interface IdName {
    id: string;
    name: string;
}

export default function NewRestaurantModal(props: NewRestaurantModalProps) {
    const history = useHistory();
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState<IdName[]>([]);

    const allOptions = useRef<IdName[]>([]);

    const validationSchema = Yup.object().shape({
        type: Yup.string().required(),
        selection: Yup.mixed()
            .nullable()
            .when('type', {
                is: 'show',
                then: Yup.object()
                    .nullable()
                    .required(
                        i18n.t('form.errors.required', {
                            name: 'Restaurant',
                        })
                    ),
                otherwise: Yup.mixed().nullable(),
            }),
    });

    const initialValues: FormValues = {
        type: 'none',
        selection: null,
    };

    useEffect(() => {
        setOptions(
            allOptions.current.filter((x) =>
                x.name.toLowerCase().includes(inputValue.toLowerCase())
            )
        );
    }, [inputValue]);

    useEffect(() => {
        agent.Restaurants.GetSimpleList()
            .then((response) => {
                setOptions(response);
                allOptions.current = response;
            })
            .catch((e) => {
                globalStore.actionCreators.showToaster('error', e);
            })
            .finally(() => globalStore.actionCreators.hideSpiner());
    }, []);

    return (
        <Dialog open={props.isVisible}>
            <DialogTitle>{i18n.t('restaurant.createRestaurant')}</DialogTitle>
            <DialogContent>
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={(values) => {
                        const url = values.selection
                            ? `/restaurants/${values.selection.id}/copy`
                            : '/createrestaurant';
                        history.push(url);
                    }}
                >
                    {({ values, setFieldValue, errors, touched, isSubmitting }) => (
                        <Form>
                            <FormControl component="fieldset">
                                <Field name="type">
                                    {({ field }: any) => (
                                        <RadioGroup {...field}>
                                            <FormControlLabel
                                                value="none"
                                                control={<Radio color="primary" />}
                                                label={i18n.t('restaurant.createNew')}
                                            />
                                            <FormControlLabel
                                                value="show"
                                                control={<Radio color="primary" />}
                                                label={i18n.t('restaurant.copyExisting')}
                                            />
                                        </RadioGroup>
                                    )}
                                </Field>
                            </FormControl>

                            {values.type === 'show' && (
                                <Box mt={2}>
                                    <Autocomplete
                                        options={options}
                                        getOptionLabel={(option) => option.name}
                                        onInputChange={(_, val) => setInputValue(val)}
                                        onChange={(_, val) => setFieldValue('selection', val)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={
                                                    touched.selection && Boolean(errors.selection)
                                                        ? errors.selection
                                                        : i18n.t('common.selectFromTheList')
                                                }
                                                variant="filled"
                                                error={
                                                    touched.selection && Boolean(errors.selection)
                                                }
                                                helperText={touched.selection && errors.selection}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>{params.InputProps.endAdornment}</>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            )}
                            <DialogActions>
                                <Button type="button" color="primary" onClick={props.onClose}>
                                    {i18n.t('button.cancel')}
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={
                                        (values.type === 'show' && !values.selection) ||
                                        isSubmitting
                                    }
                                >
                                    {i18n.t('button.confirm')}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    );
}
